import PropTypes from "prop-types";
import * as React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { routePath } from "../../../shared/routes/index.js";
import {
  INITIAL_SIGN_IN,
  signInStep,
  startSecondFactor,
} from "../../domain/signInFlow";
import useRememberedEmail from "../../hooks/useRememberedEmail";
import {
  authenticate,
  messageOf,
  passkeySignIn,
  passkeySignInOptions,
  secondFactor,
  secondFactorPasskeyOptions,
} from "../../services/auth";
import {
  cancelPasskeyRequest,
  supportsPasskeyAutofill,
} from "../../services/passkeys";
import { Alert, Box } from "../../themeV2";
import { passkeyProof } from "../Mfa/passkeyProof";
import CredentialsStep from "./CredentialsStep";
import EmailStep from "./EmailStep";
import SecondFactorStep from "./SecondFactorStep";

const PENDING_EXPIRED = "PENDING_EXPIRED";

const initialState = ({ pending, rememberedEmail }) =>
  pending
    ? startSecondFactor(pending.methods)
    : rememberedEmail
    ? signInStep(INITIAL_SIGN_IN, {
        type: "remembered",
        email: rememberedEmail,
      })
    : INITIAL_SIGN_IN;

const secureAccountPath = (search) => {
  const r = new URLSearchParams(search).get("r");

  return `${routePath("auth.secure-account")}${
    r ? `?${new URLSearchParams({ r })}` : ""
  }`;
};

const signInWithPasskey = async (options) => {
  const { proof, cancelled, failure } = await passkeyProof(
    passkeySignInOptions,
    options,
  );

  return proof
    ? passkeySignIn({ ceremonyId: proof.ceremonyId, response: proof.response })
    : { ok: false, cancelled, data: { message: failure } };
};

const useAutofillPasskey = ({ active, onResult }) => {
  React.useEffect(() => {
    const live = { current: true };

    supportsPasskeyAutofill().then((supported) =>
      supported && active
        ? signInWithPasskey({ autofill: true }).then(
            (result) => live.current && !result.cancelled && onResult(result),
          )
        : null,
    );

    return () => {
      live.current = false;
      cancelPasskeyRequest();
    };
  }, [active, onResult]);
};

export default function SignInFlow({ pending, onSignedIn }) {
  const history = useHistory();
  const location = useLocation();
  const remembered = useRememberedEmail();
  const [state, dispatch] = React.useReducer(
    signInStep,
    { pending, rememberedEmail: remembered.email },
    initialState,
  );
  const [failure, setFailure] = React.useState(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleResult = React.useCallback((result) => {
    setIsSubmitting(false);

    if (result.ok) {
      setFailure(null);
      return dispatch({ type: "answered", body: result.data });
    }

    if (result.data?.code === PENDING_EXPIRED) dispatch({ type: "expired" });

    return result.cancelled ? null : setFailure(messageOf(result));
  }, []);

  const send = (request) => {
    setFailure(null);
    setIsSubmitting(true);
    return request().then(handleResult);
  };

  useAutofillPasskey({
    active: state.step === "email",
    onResult: handleResult,
  });

  React.useEffect(() => {
    if (state.step === "enrol") {
      history.push(secureAccountPath(location.search));
    }
    if (state.step === "done") {
      remembered.remember(state.email || state.user?.email);
      onSignedIn(state.user);
    }
  }, [state.step]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeAccount = () => {
    remembered.forget();
    setFailure(null);
    dispatch({ type: "change_account" });
  };

  return (
    <Box direction="column" gap={4}>
      {state.step === "email" && (
        <EmailStep
          onContinue={(email) => dispatch({ type: "email_entered", email })}
        />
      )}

      {state.step === "credentials" && (
        <CredentialsStep
          email={state.email}
          isSubmitting={isSubmitting}
          onChangeAccount={changeAccount}
          onPassword={(password) =>
            send(() =>
              authenticate({ mode: "login", email: state.email, password }),
            )
          }
          onPasskey={() => send(() => signInWithPasskey())}
        />
      )}

      {state.step === "second_factor" && (
        <SecondFactorStep
          methods={state.methods}
          method={state.method}
          isSubmitting={isSubmitting}
          onChoose={(method) => {
            setFailure(null);
            dispatch({ type: "method_chosen", method });
          }}
          onCode={(proof) => send(() => secondFactor(proof))}
          onPasskey={() =>
            send(async () => {
              const {
                proof,
                cancelled,
                failure: problem,
              } = await passkeyProof(secondFactorPasskeyOptions);

              return proof
                ? secondFactor(proof)
                : { ok: false, cancelled, data: { message: problem } };
            })
          }
        />
      )}

      {failure && <Alert severity="error">{failure}</Alert>}
    </Box>
  );
}

SignInFlow.propTypes = {
  pending: PropTypes.shape({
    methods: PropTypes.arrayOf(PropTypes.string).isRequired,
  }),
  onSignedIn: PropTypes.func.isRequired,
};
